"use client";
// Helper to build Cloudflare R2 URL
function getMediaUrl(filename) {
  // Always use Cloudflare R2 for media
  if (!filename) return '';
  // If already a full R2 URL, return as is
  if (typeof filename === 'string' && filename.startsWith('https://pub-0705ed3bb3a5425799ba64b1bd76a81b.r2.dev/shared_media/')) {
    return filename;
  }
  // Otherwise, encode and build R2 URL
  return `https://pub-0705ed3bb3a5425799ba64b1bd76a81b.r2.dev/shared_media/${encodeURI(filename.replace(/^\/+/, ""))}`;
}

import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";



const brands = [
  "Logos/31.png",
  "Logos/32.png",
  "Logos/34.png",
  "Logos/35.png",
  "Logos/36.png",
  "Logos/9.png",
  "Logos/10.png",
  "Logos/11.jpg",
  "Logos/22.jpeg",
  "Logos/23.png",
  "Logos/24.png",
  "Logos/6.png",
  "Logos/7.jpg",
  "Logos/8.png",
  "Logos/neo.png",
  "Logos/aux_logo.jpg",
  "Logos/Bilsan.jpg",
  "Logos/LG.svg",
  "Logos/Media.png",
  "Logos/Aquafina.png",
  "Logos/Cola.png",
  "Logos/Doritos_Logo_(2013).png",
  "Logos/Eti_logo.png",
  "Logos/etihad logo.jpg",
  "Logos/Fanta.png",
  "Logos/Nestle.png",
  "Logos/Pepsi.jpg",
  "Logos/Rani.jpg",
  "Logos/dzone.png",
  "Logos/jazeera.png",
  "Logos/Listerine.png",
  "Logos/Pioneer logo.jpg",
  "Logos/Selpak.jpg",
  "Logos/Unicf.png",
  "Logos/WEP.png",
  "Logos/12.jpg",
  "Logos/13.png",
  "Logos/14.png",
  "Logos/15.png",
  "Logos/16.png",
  "Logos/17.png",
  "Logos/18.png",
  "Logos/19.jpg",
  "Logos/Modon logo.png",
  "Logos/MPC logo.png",
  "Logos/30.jpg",
  "Logos/Apple.png",
  "Logos/Honor.png",
  "Logos/4.png",
  "Logos/5.png",
  "Logos/Asiacell.png",
  "Logos/Zain.png",
];

// دالة تقسيم المصفوفة إلى أجزاء
const chunkArray = (arr, size) => {
  const result = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
};

// Helper function to get brand groups
const getBrandGroups = (brandsArray) => {
  return chunkArray(brandsArray, Math.ceil(brandsArray.length / 3));
};

const BrandsSection = ({ data }) => {
  // Fallback to hardcoded values if data is not available
  const title = data?.title || "عملائنا";
  const subtitle = data?.subtitle || "يسعدنا أن نتعاون مع أكبر العلامات التجارية في العراق.";
  
  // Filter out logos with invalid/null images
  const logosData = Array.isArray(data?.logos) && data.logos.length > 0
    ? data.logos.filter(logo => {
        const imageUrl = logo?.image?.url || logo?.image;
        return imageUrl && imageUrl !== null && imageUrl !== undefined && imageUrl !== "";
      })
    : brands.map(logo => ({ image: getMediaUrl(logo) }));
    
  // If no valid logos, use fallback brands
  const validLogos = logosData.length > 0 
    ? logosData 
    : brands.map(logo => ({ image: getMediaUrl(logo) }));

  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true });

  return (
  <section
    ref={sectionRef}
    className="py-20 overflow-hidden w-full"
    dir="ltr"
  >
        <div className="w-full ">
          {/* Header */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            dir="ltr"
          >
            <h2 className="text-6xl font-bold text-yellow-500 mb-6">{title}</h2>
            <p className="text-2xl text-black">
              {subtitle}
            </p>
          </motion.div>

          {/* Infinite Scrolling Brands */}
          <div className="space-y-8">
            {getBrandGroups(validLogos.map(logo => logo.image?.url || logo.image || logo)).map((group, rowIndex) => {
              // Calculate exact width: (logo width + gap) * number of logos
              const totalWidth = (128 + 32) * group.length; // 128px logo + 32px gap (space-x-8)
              return (
                <div key={rowIndex} className="relative overflow-hidden">
                  <motion.div
                    className="flex space-x-8 whitespace-nowrap"
                    animate={{
                      x: rowIndex % 2 === 0 ? [-totalWidth, 0] : [0, -totalWidth]
                    }}
                    transition={{
                      duration: 30,
                      repeat: Infinity,
                      ease: "linear",
                      repeatType: "loop",
                    }}
                  >
                    {/* First set of logos */}
                    {group.map((brand, index) => (
                      <BrandCard key={`row-${rowIndex}-${index}`} brand={brand} />
                    ))}
                    {/* Duplicate set for seamless loop */}
                    {group.map((brand, index) => (
                      <BrandCard key={`row-${rowIndex}-duplicate-${index}`} brand={brand} />
                    ))}
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  const [imageLoaded, setImageLoaded] = useState(false);

  // Extract filename from object or string
  let filename = "";
  if (typeof brand === "object" && brand !== null) {
    filename = brand.image?.url || brand.image || "";
  } else {
    filename = brand;
  }
  if (!filename || imageError) {
    return null;
  }
  // Always use getMediaUrl to prevent double prefix
  const imageUrl = getMediaUrl(filename);

  // SVG support: use <object> for SVG, <img> for others
  const isSvg = typeof imageUrl === 'string' && imageUrl.trim().toLowerCase().endsWith('.svg');

  return (
    <motion.div
      className="flex-shrink-0 w-32 h-32 rounded-2xl flex items-center justify-center p-4 hover:shadow-xl transition-shadow duration-300"
      whileHover={{ scale: 1.05, y: -5 }}
      transition={{ duration: 0.3 }}
      style={{ display: imageError ? 'none' : 'flex' }}
    >
      <img
        src={imageError ? '/fallback-image.png' : imageUrl}
        alt="Brand"
        className="max-w-full max-h-full object-contain hover:grayscale-0 transition-all duration-300"
        width={128}
        height={128}
        loading="eager"
        style={{ display: imageError ? 'block' : 'block' }}
        onError={() => {
          if (!imageError) {
            console.error("Failed to load brand image:", imageUrl);
            setImageError(true);
          }
        }}
        onLoad={() => setImageLoaded(true)}
      />
    </motion.div>
  );
};


// BrandCard component for rendering each brand logo
const BrandCard = ({ brand }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Extract filename from object or string
  let filename = "";
  if (typeof brand === "object" && brand !== null) {
    filename = brand.image?.url || brand.image || "";
  } else {
    filename = brand;
  }
  if (!filename || imageError) {
    return null;
  }
  // Always use getMediaUrl to prevent double prefix
  const imageUrl = getMediaUrl(filename);

  return (
    <motion.div
      className="flex-shrink-0 w-32 h-32 rounded-2xl flex items-center justify-center p-4 hover:shadow-xl transition-shadow duration-300"
      whileHover={{ scale: 1.05, y: -5 }}
      transition={{ duration: 0.3 }}
      style={{ display: imageError ? 'none' : 'flex' }}
    >
      <img
        src={imageError ? '/fallback-image.png' : imageUrl}
        alt="Brand"
        className="max-w-full max-h-full object-contain hover:grayscale-0 transition-all duration-300"
        width={128}
        height={128}
        loading="eager"
        style={{ display: imageError ? 'block' : 'block' }}
        onError={() => {
          if (!imageError) {
            console.error("Failed to load brand image:", imageUrl);
            setImageError(true);
          }
        }}
        onLoad={() => setImageLoaded(true)}
      />
    </motion.div>
  );
};

export default BrandsSection;
